import { ReactNode } from 'react'
import { Box, Typography, Alert } from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import { isAdmin, isTeamMember, isManager, hasFeatureAccess } from '../services/auth'

interface RoleGuardProps {
  children: ReactNode
  requiredRole?: 'admin' | 'team' | 'manager' | 'any'
  requiredFeature?: string
  fallback?: ReactNode
}

export default function RoleGuard({
  children,
  requiredRole = 'any',
  requiredFeature,
  fallback
}: RoleGuardProps) {
  const { user, isAuthenticated } = useAuth()

  // If not authenticated, don't render anything (API client will redirect to login)
  if (!isAuthenticated || !user) {
    return null
  }

  // Feature-based check takes priority when specified
  let hasPermission = false

  if (requiredFeature) {
    hasPermission = hasFeatureAccess(user, requiredFeature)
  } else {
    switch (requiredRole) {
      case 'admin':
        hasPermission = isAdmin(user)
        break
      case 'team':
        hasPermission = isTeamMember(user)
        break
      case 'manager':
        hasPermission = isManager(user)
        break
      case 'any':
      default:
        hasPermission = true
        break
    }
  }

  if (!hasPermission) {
    if (fallback) {
      return <>{fallback}</>
    }

    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <Typography variant="h6" gutterBottom>
            🚫 Access Restricted
          </Typography>
          <Typography variant="body1">
            You don't have permission to access this feature. Your current role is: <strong>{user.role}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Contact your administrator if you believe this is an error.
          </Typography>
        </Alert>
      </Box>
    )
  }

  return <>{children}</>
}